<template>
  <div
    :class="[heightClass, backgroundGradientClass]"
    class="previousLegInfo leg--previous-detail w-full flex flex-row items-center px-2"
  >
    <div class="previousLegInfo__icon">
      <Icon v-if="data.size.xl" :name="previousLegIcon" width="24" height="24" />
    </div>
    <div class="flex justify-between items-center flex-1 pl-4 pr-4">
      <div class="flex flex-1 flex-row flex-wrap leading-4">
        <div class="previousLegInfo__time font-semibold mr-4">
          {{ arrivalDate }}
        </div>
        <div class="previousLegInfo__from font-semibold" :class="{ 'mx-auto': data.size.xl || data.size.lg }">
          {{ legTypeAbbreviation }}
        </div>
      </div>
      <div
        v-if="!data.size.sm && !data.size.xs"
        class="previousLegInfo__departure font-semibold flex flex-wrap leading-4 ml-1"
      >
        <div>{{ `${departureInfo.departureDate}` }}</div>
        <!-- remove v-if condition after vehicle number is implemented... -->
        <div v-if="departureInfo.vehicleNumber !== 'Not done yet'">{{ `/${departureInfo.vehicleNumber}` }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { PreviousLegDetails } from "@/types/leg";
import { defineLegTypeName, defineLegTypeIcon } from "@/use/useLeg";
import { dateStringToShowDate } from "@/use/useDate";
import Icon from "@/components/Icon.vue";

export default defineComponent({
  name: "PreviousLegDetail",
  components: {
    Icon
  },
  props: {
    data: {
      type: Object as () => PreviousLegDetails,
      required: true
    },
    show: {
      type: Boolean,
      required: true
    },
    hasIrregularities: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const heightClass = computed(() => (props.show && "details-height") || "h-0");
    const DEFAULT_TEXT = "-";

    const arrivalDate = computed(() => {
      const { effectiveArrivalDate, plannedArrivalDate } = props.data;
      const date = effectiveArrivalDate?.date || plannedArrivalDate?.date;

      if (!date) {
        return "";
      }
      return date && dateStringToShowDate(date, "dd.MM/HH:mm");
    });

    const departureInfo = computed(() => {
      const { effectiveDepartureDate, intermodalVehicleNumber } = props.data;
      const departureDate =
        (effectiveDepartureDate?.date && dateStringToShowDate(effectiveDepartureDate?.date, "dd.MM")) || DEFAULT_TEXT;
      const vehicleNumber = intermodalVehicleNumber || DEFAULT_TEXT;
      return { departureDate, vehicleNumber };
    });

    const previousLegIcon = computed(() => {
      return defineLegTypeIcon(props.data?.legType);
    });

    const backgroundGradientClass = computed(() => {
      const { arrived, booked, departed } = props.data.state || {};
      return {
        "gradient-yellow": departed,
        "gradient-green": arrived,
        "gradient-gray": booked,
        "gradient-red": props.hasIrregularities
      };
    });

    const legTypeAbbreviation = computed(() => {
      return `${defineLegTypeName(props.data?.legType)}-${props.data?.from?.name || ""}`;
    });

    return {
      heightClass,
      backgroundGradientClass,
      previousLegIcon,
      arrivalDate,
      departureInfo,
      legTypeAbbreviation
    };
  }
});
</script>

<style lang="scss" scoped>
@use "PreviousLegDetail";
.details-height {
  height: 43%;
}
</style>
